import { useMarketLocation } from '@e2m-customer-portal/hooks';
import { ErrorComponent, Loading, PageTitle, Toolbar } from '@e2m-customer-portal/ui';
import { startOfMonth } from 'date-fns';
import { ReactElement, ReactNode, Suspense, useContext, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { MonthSingleSelector } from './month-single-selector/MonthSingleSelector';
import { MonthlyRevenueTable } from './monthly-revenue-table/MonthlyRevenueTable';
import { SelectedMonthContext } from './RevenueReportPage';

export interface MonthlyRevenueProps {
  marketLocationId: string;
  pageTitleActions?: ReactNode;
  sidebarOffset?: number;
}

export function MonthlyRevenue({
  marketLocationId,
  pageTitleActions,
  sidebarOffset = 0,
}: Readonly<MonthlyRevenueProps>): ReactElement<MonthlyRevenueProps> {
  const [t] = useTranslation();
  const [marketLocation, error] = useMarketLocation(marketLocationId);
  const { setSelectedMonth } = useContext(SelectedMonthContext);

  const [selectedDate, setSelectedDate] = useState<Date>(startOfMonth(new Date()));

  useEffect(() => {
    setSelectedMonth({
      id: selectedDate.toISOString(),
      date: selectedDate,
      name: t('REVENUE_DOWNLOAD_MONTH', { date: selectedDate }),
    });
  }, [selectedDate]);

  if (!marketLocation || error) {
    throw new Error('Failed to retrieve market location data');
  }

  return (
    <>
      <div className="md:sticky md:z-[1202] md:bg-white" style={{ top: `${sidebarOffset}px` }}>
        <PageTitle
          title={t('REVENUE_REPORT_TABLE_TITLE', { name: marketLocation.externalName || marketLocation.name })}
        />
        <Toolbar
          left={
            <MonthSingleSelector
              setMonth={setSelectedDate}
              marketingStart={marketLocation.marketingStart}
              marketingEnd={marketLocation.marketingEnd}
            />
          }
          right={pageTitleActions}
        />
      </div>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <Suspense fallback={<Loading />}>
          <MonthlyRevenueTable
            marketLocationId={marketLocationId}
            selectedDate={selectedDate}
            technologies={marketLocation.technologies}
            sidebarOffset={sidebarOffset}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
